/**
 * Connect — Algomed ProfilePage > UserInfoDisplay desenini Telegram WebApp'e
 * uyarladığımız identity card. Ayrıca eşleştirme akışı için 6-haneli kod bloğu.
 *
 * Mirror:
 * - Avatar + ad + tier badge (ProfilePage Flex direction column header)
 * - "Telegram'ı bağla" CTA card · Kod oluştur → 6-digit display
 * - Ek bilgi kartı (`bgCard` + `borderPrimary`)
 *
 * Telegram MainButton: aktif (Bağlı değil iken) "Kod Oluştur" → kod aldıktan
 * sonra "Bota Yapıştır" deeplink açar.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Card,
  CardBody,
  Code,
  Container,
  Divider,
  HStack,
  Heading,
  Spinner,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { api } from '../lib/api';
import { tg, getTelegramUser, haptic, setMainButton } from '../telegram';
import { BRAND, ICON, type Tier } from '@algobot/shared';
import PlanBadge from '../components/PlanBadge';

// Canonical Me tipini lib/api.ts'den al — algomedUserId/subscriptionEndsAt
// gibi yeni alanlari otomatik kullan.
import type { MeResp as Me } from '../lib/api';

const BOT_DEEP_LINK = (code: string) => `https://t.me/algomed_bot?start=baglan_${code}`;

export default function ConnectPage() {
  const tgUser = getTelegramUser();
  const [me, setMe] = useState<Me | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [meLoading, setMeLoading] = useState(true);
  const [error, setError] = useState<{ status?: number; message: string } | null>(null);
  const [linking, setLinking] = useState(false);
  const toast = useToast();

  const load = async () => {
    setMeLoading(true);
    setError(null);
    try {
      const m = await api.me();
      setMe(m);
    } catch (err: any) {
      const status = err?.status as number | undefined;
      setError({
        status,
        message:
          status === 401
            ? 'Bu mini uygulama Telegram içinde açılmalı.'
            : 'Hesap bilgileri alınamadı.',
      });
    } finally {
      setMeLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startLink = async () => {
    setLinking(true);
    haptic('select');
    try {
      const r = await api.startLink();
      setCode(r.code);
      setExpiresAt(r.expiresAt);
      haptic('success');
    } catch {
      toast({ title: 'Kod oluşturulamadı', status: 'error', duration: 3000 });
      haptic('error');
    } finally {
      setLinking(false);
    }
  };

  const tier = (me?.tier || 'free').toLowerCase() as Tier;
  const linked = !!me?.linked && !me?.shadow;

  const displayName =
    me?.displayName || tgUser?.first_name || tgUser?.username || 'Algomedli';
  const handle = tgUser?.username ? `@${tgUser.username}` : null;

  const expiresLabel = useMemo(() => {
    if (!expiresAt) return null;
    try {
      const d = new Date(expiresAt);
      const mins = Math.max(0, Math.round((d.getTime() - Date.now()) / 60000));
      return mins > 0 ? `${mins} dakika içinde geçerliliğini yitirir` : 'Süresi doldu';
    } catch {
      return null;
    }
  }, [expiresAt]);

  // MainButton: bağlı değilken kod akışını yönet.
  useEffect(() => {
    if (meLoading || error) return;
    if (linked) {
      // Bağlıyken MainButton yok — sadece kapatma için close gösterilir.
      return setMainButton('Kapat', () => tg()?.close());
    }
    if (!code) {
      return setMainButton('Kod Oluştur', () => {
        haptic('select');
        startLink();
      });
    }
    return setMainButton('Bota Yapıştır', () => {
      haptic('select');
      try {
        (window as any).Telegram?.WebApp?.openTelegramLink?.(BOT_DEEP_LINK(code));
      } catch {
        window.location.href = BOT_DEEP_LINK(code);
      }
    });
  }, [meLoading, error, linked, code]);

  if (meLoading) {
    return (
      <Container maxW="md" py={10}>
        <VStack spacing={3}>
          <Spinner size="xl" color="accent" thickness="3px" />
          <Text color="textSecondary" fontSize="sm">
            Algomed yükleniyor…
          </Text>
        </VStack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="md" py={6}>
        <Alert
          status="warning"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          textAlign="center"
          borderRadius="xl"
          py={6}
          bg="bgCard"
          borderWidth="1px"
          borderColor="borderPrimary"
        >
          <AlertIcon boxSize="32px" color="warningIcon" />
          <AlertTitle mt={3} fontSize="md" color="textPrimary">
            {error.status === 401 ? 'Telegram içinde aç' : 'Bağlanılamadı'}
          </AlertTitle>
          <AlertDescription fontSize="sm" color="textSecondary" mt={2}>
            {error.message}
          </AlertDescription>
          <Button mt={4} colorScheme="brand" size="sm" onClick={load}>
            Tekrar dene
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="md" py={4} px={3}>
      <VStack spacing={4} align="stretch">
        {/* IDENTITY CARD · Algomed ProfilePage UserInfoDisplay muadili */}
        <Card variant="elevated">
          <CardBody>
            <VStack spacing={3} align="center" textAlign="center">
              <Avatar
                size="xl"
                name={displayName}
                src={tgUser?.photo_url}
                bg="brand.500"
                color="white"
              />
              <VStack spacing={1}>
                <Heading size="md" color="textPrimary">
                  {displayName}
                </Heading>
                {handle ? (
                  <Text fontSize="sm" color="textSecondary">
                    {handle}
                  </Text>
                ) : null}
                <HStack spacing={2} pt={1}>
                  <PlanBadge tier={tier} size="md" />
                  {linked ? (
                    <Box
                      px={2}
                      py={0.5}
                      borderRadius="full"
                      bg="successBg"
                      borderWidth="1px"
                      borderColor="successIcon"
                    >
                      <HStack spacing={1}>
                        <Text fontSize="sm" color="successIcon" lineHeight="1">
                          {ICON.ok}
                        </Text>
                        <Text fontSize="xs" color="successText" fontWeight="semibold">
                          Bağlı
                        </Text>
                      </HStack>
                    </Box>
                  ) : null}
                </HStack>
              </VStack>
            </VStack>
          </CardBody>
        </Card>

        {/* CONNECT CTA */}
        {linked ? (
          <Card variant="elevated">
            <CardBody>
              <VStack spacing={2} align="start">
                <HStack spacing={2}>
                  <Text fontSize="lg" lineHeight="1" color="successIcon">
                    {ICON.ok}
                  </Text>
                  <Heading size="sm" color="textPrimary">
                    Hesap bağlı
                  </Heading>
                </HStack>
                <Text fontSize="sm" color="textSecondary">
                  Telegram hesabın Algomed web hesabınla eşleştirildi. İlerlemen
                  tek hesapta birikiyor.
                </Text>
              </VStack>
            </CardBody>
          </Card>
        ) : (
          <Card variant="elevated">
            <CardBody>
              <VStack spacing={3} align="stretch">
                <HStack spacing={2}>
                  <Text fontSize="lg" lineHeight="1" color="accent">
                    {ICON.link}
                  </Text>
                  <Heading size="sm" color="textPrimary">
                    {BRAND.copy.linkCta}
                  </Heading>
                </HStack>
                <Text fontSize="sm" color="textSecondary">
                  Aşağıdaki butona dokun, 6 haneli kod oluşur. 5 dakika içinde
                  bota <Code>/baglan KOD</Code> yazınca eşleşme tamamlanır.
                </Text>

                {!code ? (
                  <Button
                    colorScheme="brand"
                    size="md"
                    onClick={startLink}
                    isLoading={linking}
                  >
                    Kod oluştur
                  </Button>
                ) : (
                  <VStack align="stretch" spacing={2}>
                    <Box
                      bg="bgSecondary"
                      border="1px solid"
                      borderColor="borderPrimary"
                      borderRadius="lg"
                      py={4}
                      textAlign="center"
                    >
                      <Text fontSize="xs" color="textMuted" mb={1}>
                        Kodun:
                      </Text>
                      <Heading
                        size="2xl"
                        letterSpacing="0.3em"
                        color="accent"
                        fontFamily="mono"
                      >
                        {code}
                      </Heading>
                      {expiresLabel ? (
                        <Text fontSize="11px" color="textMuted" mt={2}>
                          {expiresLabel}
                        </Text>
                      ) : null}
                    </Box>
                    <Text fontSize="xs" color="textSecondary" textAlign="center">
                      Bota şöyle yaz: <Code>/baglan {code}</Code>
                    </Text>
                    <Button
                      variant="outline"
                      colorScheme="brand"
                      size="sm"
                      onClick={() => {
                        haptic('select');
                        try {
                          (window as any).Telegram?.WebApp?.openTelegramLink?.(
                            BOT_DEEP_LINK(code),
                          );
                        } catch {
                          window.location.href = BOT_DEEP_LINK(code);
                        }
                      }}
                    >
                      Bota yapıştır
                    </Button>
                  </VStack>
                )}
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* INFO CARD */}
        <Card variant="elevated">
          <CardBody>
            <VStack align="start" spacing={2}>
              <HStack spacing={2}>
                <Text fontSize="md" lineHeight="1" color="textMuted">
                  {ICON.brand}
                </Text>
                <Heading size="sm" color="textPrimary">
                  Algomed nedir?
                </Heading>
              </HStack>
              <Divider borderColor="borderPrimary" />
              <Text fontSize="xs" color="textSecondary" lineHeight="1.5">
                Algomed, YDUS/TUS hazırlık sürecini soru bankası, flashcard,
                AlgoSpot notları ve dijital antrenör/mentor modülleriyle
                birleştirir. Web sitesi ile aynı içerik Mini App'te elinin altında.
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Container>
  );
}
